import React, { useEffect, useRef, useState } from 'react'
import {
    Badge,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Popover,
    Switch,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'

import { Container, MessageDrawer, SideDrawer } from '../../components'
import {
    AreaInspection,
    AreaOrder,
    AreaStatusTag,
    getAssignedUsersDisplay,
    InventoryConfig,
    InventoryInspection,
    ModelMap,
    UnitConfig,
    UnitInspection,
} from '../../models'

import { UnitInspectionStatusBadge } from './UnitInspectionStatusBadge'
import { InventoryInspectionComponent } from './InventoryInspectionComponent'
import {
    bulkHandleInventoryInspections,
    updateInventoryInspection,
    updateUnitInspection,
    updateUnitInspectionStatus,
} from '../../store'
import { Flag, Info } from '@material-ui/icons'
import { useAppDispatch, useInspection } from '../../hooks'
import { axiosInstance, containsOneOf } from '../../helpers'
import { toast } from 'react-toastify'
import { useVideoInspection } from '../../hooks/useVideoInspection'
import { LoriText } from '../../navigation/layouts/Lori/Icons/LoriText'
import { typographyStyle } from '../../theme/typographyStyle'

function isComArea(name: string): boolean {
    return /^com\b/i.test(name.trim())
}
function isBedArea(name: string): boolean {
    return /\b(bed|bedroom)\b/i.test(name)
}
function isBathArea(name: string): boolean {
    return /\bbath\b/i.test(name)
}

const sortAreaInspectionsByOrder = (
    areaInspections: AreaInspection[],
    areaOrder: AreaOrder,
): AreaInspection[] => {
    const sorted = [...areaInspections]
    switch (areaOrder) {
        case AreaOrder.BY_CREATION:
            return sorted
        case AreaOrder.ALPHABETICAL:
            return sorted.sort((a, b) => {
                const aName = `${
                    a.area.area_config?.name ?? a.area.area_config_name ?? ''
                } ${a.area.area_label}`
                const bName = `${
                    b.area.area_config?.name ?? b.area.area_config_name ?? ''
                } ${b.area.area_label}`
                return aName.localeCompare(bName)
            })
        case AreaOrder.COM_BED_BATH_COMBO:
            return sorted.sort((areaInsA, areaInsB) => {
                const aName = `${
                    areaInsA.area.area_config?.name ??
                    areaInsA.area.area_config_name ??
                    ''
                } ${areaInsA.area.area_label}`.trim()
                const bName = `${
                    areaInsB.area.area_config?.name ??
                    areaInsB.area.area_config_name ??
                    ''
                } ${areaInsB.area.area_label}`.trim()

                const aIsCom = isComArea(aName)
                const bIsCom = isComArea(bName)
                const aIsBed = isBedArea(aName)
                const bIsBed = isBedArea(bName)
                const aIsBath = isBathArea(aName)
                const bIsBath = isBathArea(bName)

                if (aIsCom && !bIsCom) return -1
                if (!aIsCom && bIsCom) return 1

                if (aIsCom && bIsCom) {
                    return aName.localeCompare(bName)
                }

                const labelCompare = areaInsA.area.area_label.localeCompare(
                    areaInsB.area.area_label,
                )
                if (labelCompare !== 0) return labelCompare

                if (aIsBed && bIsBath) return -1
                if (aIsBath && bIsBed) return 1

                const aConfigName =
                    areaInsA.area.area_config?.name ??
                    areaInsA.area.area_config_name ??
                    ''
                const bConfigName =
                    areaInsB.area.area_config?.name ??
                    areaInsB.area.area_config_name ??
                    ''
                return aConfigName.localeCompare(bConfigName)
            })
        default:
            return sorted
    }
}

interface Props {
    unitConfigMap: ModelMap<UnitConfig>
    inventoryConfigMap: ModelMap<InventoryConfig>
    unitInspection?: UnitInspection
    areaStatusMap: ModelMap<AreaStatusTag>
    open: boolean
    loading?: boolean
    isInventoryInspectionInFilter: (invIns: InventoryInspection) => boolean
    onClose: () => void
    setUnitInspection?: (unitInspection: UnitInspection) => void
}

export const UnitInspectionDetailForm = (props: Props) => {
    const {
        unitInspection,
        onClose,
        unitConfigMap,
        inventoryConfigMap,
        areaStatusMap,
        open,
        isInventoryInspectionInFilter,
        setUnitInspection,
    } = props

    const dispatch = useAppDispatch()

    const [messagesOpen, setMessagesOpen] = useState(false)
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setAnchorEl(null)
    }
    const popOverOpen = Boolean(anchorEl)
    const [selectedInvIns, setSelectedInvIns] = useState<
        InventoryInspection | undefined
    >(undefined)

    const [editMode, setEditMode] = useState(false)

    interface AreaInsSelectionDict {
        areaIns: AreaInspection
        selected: boolean
    }

    const [reportAreaSelectedList, setReportAreaSelectedList] = useState<
        AreaInsSelectionDict[]
    >([])
    const [selectedAreaTab, setSelectedAreaTab] = useState<number>(0)
    const unitInspectionId = unitInspection?.id ?? -1
    const {
        analyzeWithLori,
        isUpdating: isAnalyzingWithLori,
        loriTaskStatus,
        isPollingLoriTask,
        resetLoriTask,
    } = useVideoInspection(unitInspectionId, open && unitInspectionId > 0)

    useEffect(() => {
        const areaInspectionList = unitInspection?.area_inspections ?? []
        if (areaInspectionList.length > 0) {
            const newSelectionArray: AreaInsSelectionDict[] = []
            areaInspectionList.forEach((area) => {
                newSelectionArray.push({ areaIns: area, selected: false })
            })
            setReportAreaSelectedList(newSelectionArray)
            setSelectedAreaTab((previous) => {
                return previous < areaInspectionList.length ? previous : 0
            })
        } else {
            setReportAreaSelectedList([])
            setSelectedAreaTab(0)
        }
    }, [unitInspection?.area_inspections])

    const { inventoryInspectionMap } = useInspection(-1)

    const theme = useTheme()

    const unitConfigName = unitInspection
        ? unitConfigMap[unitInspection.unit.unit_config]?.name
        : ''

    const areaInspectionList = sortAreaInspectionsByOrder(
        unitInspection?.area_inspections ?? [],
        AreaOrder.COM_BED_BATH_COMBO,
    )

    const selectedAreaInspection = areaInspectionList[selectedAreaTab]
    const isLoriRunning =
        loriTaskStatus === 'PENDING' ||
        loriTaskStatus === 'STARTED' ||
        isPollingLoriTask
    const isAnalyzeWithLoriDisabled =
        unitInspectionId <= 0 ||
        isAnalyzingWithLori ||
        isLoriRunning ||
        !unitInspection?.offline_inspection
    const [isAnalyzeWithLoriHovered, setIsAnalyzeWithLoriHovered] = useState(
        false,
    )
    const [
        isAnalyzeWithLoriConfirmOpen,
        setIsAnalyzeWithLoriConfirmOpen,
    ] = useState(false)
    const lastLoriTerminalStatus = useRef<'SUCCESS' | 'FAILED' | null>(null)

    const getAreaDisplayName = (areaInspection: AreaInspection): string => {
        let areaConfigName = ''
        if (areaInspection.area.area_config.name) {
            areaConfigName = areaInspection.area.area_config.name
        } else if (areaInspection.area.area_config_name) {
            areaConfigName = areaInspection.area.area_config_name
        }
        return `${areaConfigName} ${areaInspection.area.area_label}`
    }

    const inventoryInspectionsForSelectedArea: InventoryInspection[] = selectedAreaInspection
        ? inventoryInspectionMap?.[selectedAreaInspection.area.id] ?? []
        : []

    const selectedAreaNeedsHandled: number[] = []
    const selectedAreaInventoryInspectionComponents = inventoryInspectionsForSelectedArea.reduce<
        JSX.Element[]
    >((prev, invIns) => {
        if (!isInventoryInspectionInFilter(invIns)) {
            return prev
        }

        if (!invIns.handled && invIns.status?.causes_flag) {
            selectedAreaNeedsHandled.push(invIns.id)
        }

        return prev.concat(
            <InventoryInspectionComponent
                key={`INV_INS_${invIns.id}`}
                inventoryInspection={invIns}
                unitInspection={unitInspection}
                onClick={() => {
                    setMessagesOpen(true)
                    setSelectedInvIns(invIns)
                }}
                inventoryConfigMap={inventoryConfigMap ?? {}}
                editMode={editMode}
            />,
        )
    }, [])

    const selectedAreaInspectionContent = selectedAreaInspection ? (
        <Container
            style={{
                ...typographyStyle(theme.typography.subtitle1),
                padding: theme.spacing(1),
            }}
            direction="column"
        >
            {selectedAreaNeedsHandled.length > 0 && unitInspection && (
                <Container style={{ justifyContent: 'flex-end' }}>
                    <IconButton
                        onClick={() => {
                            dispatch(
                                bulkHandleInventoryInspections({
                                    body: {
                                        handled: true,
                                        inventory_inspections: selectedAreaNeedsHandled,
                                    },
                                }),
                            )
                            // update the unit inspection
                            const updatedUnitIns: UnitInspection = {
                                ...unitInspection,
                                area_inspections: unitInspection.area_inspections.map(
                                    (areaIns) => {
                                        if (
                                            areaIns.id ===
                                            selectedAreaInspection.id
                                        ) {
                                            return {
                                                ...areaIns,
                                                handled_count:
                                                    areaIns.handled_count +
                                                    selectedAreaNeedsHandled.length,
                                            }
                                        }
                                        return areaIns
                                    },
                                ),
                            }
                            dispatch(updateUnitInspection(updatedUnitIns))
                        }}
                    >
                        <Badge
                            color="secondary"
                            badgeContent={selectedAreaNeedsHandled.length}
                        >
                            <Tooltip title="Handle All">
                                <Flag />
                            </Tooltip>
                        </Badge>
                    </IconButton>
                </Container>
            )}
            {/* Inventory Inspections list */}
            <Container style={{ flexWrap: 'wrap' }}>
                {selectedAreaInventoryInspectionComponents}
            </Container>
        </Container>
    ) : null

    const refreshUnitInspectionDetail = async (): Promise<void> => {
        if (unitInspectionId <= 0 || !setUnitInspection) return
        try {
            const response = await axiosInstance.get(
                `inspection/unit-inspection/${unitInspectionId}/`,
            )
            setUnitInspection(response.data as UnitInspection)
        } catch (error) {
            console.error(
                'Error refreshing unit inspection after Lori analysis:',
                error,
            )
            toast.error(
                'Lori analysis finished, but we could not refresh this inspection yet.',
            )
        }
    }

    useEffect(() => {
        if (!open) {
            setIsAnalyzeWithLoriConfirmOpen(false)
            resetLoriTask()
            lastLoriTerminalStatus.current = null
        }
    }, [open, resetLoriTask])

    useEffect(() => {
        setIsAnalyzeWithLoriConfirmOpen(false)
        lastLoriTerminalStatus.current = null
    }, [unitInspectionId])

    useEffect(() => {
        if (loriTaskStatus === 'SUCCESS') {
            if (lastLoriTerminalStatus.current !== 'SUCCESS') {
                lastLoriTerminalStatus.current = 'SUCCESS'
                toast.success('Lori analysis completed.')
                void refreshUnitInspectionDetail()
            }
            return
        }

        if (loriTaskStatus === 'FAILED') {
            if (lastLoriTerminalStatus.current !== 'FAILED') {
                lastLoriTerminalStatus.current = 'FAILED'
                toast.error('Lori analysis failed. Please try again.')
            }
            return
        }

        if (loriTaskStatus === 'PENDING' || loriTaskStatus === 'STARTED') {
            lastLoriTerminalStatus.current = null
        }
    }, [loriTaskStatus])

    const openAnalyzeWithLoriDialog = (): void => {
        if (isAnalyzeWithLoriDisabled) return
        setIsAnalyzeWithLoriConfirmOpen(true)
    }

    const closeAnalyzeWithLoriDialog = (): void => {
        setIsAnalyzeWithLoriConfirmOpen(false)
    }

    const handleAnalyzeWithLori = async (): Promise<void> => {
        if (unitInspectionId <= 0) {
            toast.error('Unit inspection is required to analyze with Lori')
            return
        }

        setIsAnalyzeWithLoriConfirmOpen(false)
        const result = await analyzeWithLori()

        if (!result.success) {
            toast.error('Failed to start Lori analysis')
            return
        }

        toast.info(
            'Lori analysis started. This process can take a few minutes.',
        )
    }

    return (
        <>
            <SideDrawer
                open={open}
                handleClose={() => {
                    setSelectedInvIns(undefined)
                    setMessagesOpen(false)
                    onClose()
                }}
                title={
                    <Container
                        direction="column"
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 700,
                            }}
                        >
                            {unitInspection?.unit.name} Inspection
                        </span>
                        <span>{unitConfigName}</span>
                    </Container>
                }
                headerRight={
                    <Container
                        style={{
                            flexDirection: 'row',
                            marginRight: theme.spacing(1),
                        }}
                    >
                        <Tooltip title={'Details and Reporting'}>
                            <IconButton
                                onClick={(e) => {
                                    openPopover(e)
                                }}
                            >
                                <Info />
                            </IconButton>
                        </Tooltip>
                        <Popover
                            open={popOverOpen}
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                horizontal: 'left',
                                vertical: 'bottom',
                            }}
                            onClose={closePopover}
                        >
                            <Container
                                style={{
                                    width: 300,
                                    backgroundColor: theme.palette.grey[100],
                                    flexDirection: 'column',
                                    padding: theme.spacing(1),
                                }}
                            >
                                <Container style={{ flexDirection: 'column' }}>
                                    <Container>
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.h6,
                                                ),
                                                marginRight: theme.spacing(1),
                                            }}
                                        >
                                            Assigned To:{' '}
                                        </span>
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.h6,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                            }}
                                        >
                                            {' '}
                                            {getAssignedUsersDisplay(
                                                unitInspection?.assigned_users,
                                                unitInspection?.assigned_to,
                                            )}
                                        </span>
                                    </Container>

                                    <Divider />
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h6,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                        }}
                                    >
                                        Inspection Report
                                    </span>
                                    <div style={{ flex: 1 }} />
                                </Container>

                                {reportAreaSelectedList.map((areaDict) => (
                                    <FormControlLabel
                                        key={`REPORT_AREA_CHECKBOX_${areaDict.areaIns.id}`}
                                        control={
                                            <Checkbox
                                                checked={areaDict.selected}
                                                style={{
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                }}
                                                onClick={() => {
                                                    setReportAreaSelectedList(
                                                        reportAreaSelectedList.map(
                                                            (old) => {
                                                                if (
                                                                    old.areaIns
                                                                        .id ===
                                                                    areaDict
                                                                        .areaIns
                                                                        .id
                                                                ) {
                                                                    return {
                                                                        ...old,
                                                                        selected: !old.selected,
                                                                    }
                                                                }
                                                                return old
                                                            },
                                                        ),
                                                    )
                                                }}
                                            />
                                        }
                                        label={`${
                                            areaDict.areaIns.area.area_config
                                                .name ??
                                            areaDict.areaIns.area
                                                .area_config_name
                                        } ${areaDict.areaIns.area.area_label}`}
                                        labelPlacement="end"
                                        style={{ fontWeight: 'bold' }}
                                    />
                                ))}
                                <Divider />
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    style={{
                                        margin: theme.spacing(2, 2, 0, 0),
                                    }}
                                    onClick={() => {
                                        let selectedAreas = ''
                                        reportAreaSelectedList.forEach(
                                            (areaDict) => {
                                                if (areaDict.selected) {
                                                    selectedAreas += `${areaDict.areaIns.id},`
                                                }
                                            },
                                        )

                                        if (selectedAreas.length === 0) {
                                            toast.error(
                                                'Please select at least 1 area to generate a report',
                                            )
                                        } else {
                                            selectedAreas = selectedAreas.slice(
                                                0,
                                                -1,
                                            )
                                            axiosInstance.get(
                                                `inspection/unit-inspection/${unitInspection?.id}/get_unit_inspection_report/`,
                                                {
                                                    params: {
                                                        area_inspections: selectedAreas,
                                                        tz_offset: new Date().getTimezoneOffset(),
                                                    },
                                                },
                                            )
                                            toast.success(
                                                'You will receive your report via email shortly!',
                                            )
                                        }
                                    }}
                                >
                                    Create
                                </Button>
                            </Container>
                        </Popover>
                        <UnitInspectionStatusBadge
                            unitInspection={unitInspection}
                            removeAvatarCircle
                        />
                    </Container>
                }
                width={400}
            >
                <Container
                    style={{
                        margin: theme.spacing(2, 2, 0, 0),
                        fontWeight: 600,
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editMode}
                                color="primary"
                                onClick={() => setEditMode(!editMode)}
                            />
                        }
                        labelPlacement="start"
                        label="Edit Mode"
                        style={{ marginRight: theme.spacing(2) }}
                    />

                    {/* Auto fill with Lori */}
                    {unitInspection?.offline_inspection && (
                        <Container
                            direction="column"
                            style={{ alignItems: 'flex-end' }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isAnalyzeWithLoriDisabled}
                                onClick={openAnalyzeWithLoriDialog}
                                onMouseEnter={() =>
                                    setIsAnalyzeWithLoriHovered(true)
                                }
                                onMouseLeave={() =>
                                    setIsAnalyzeWithLoriHovered(false)
                                }
                                style={{
                                    textTransform: 'none',
                                    borderRadius: theme.shape.borderRadius,
                                    padding: theme.spacing(0.75, 2),
                                    minHeight: 38,
                                    backgroundColor: isAnalyzeWithLoriDisabled
                                        ? theme.palette.action
                                              .disabledBackground
                                        : isAnalyzeWithLoriHovered
                                        ? theme.palette.primary.dark
                                        : theme.palette.primary.main,
                                    color: isAnalyzeWithLoriDisabled
                                        ? theme.palette.text.disabled
                                        : theme.palette.common.white,
                                    border: 'none',
                                    boxShadow: isAnalyzeWithLoriDisabled
                                        ? 'none'
                                        : isAnalyzeWithLoriHovered
                                        ? '0 4px 12px rgba(0, 0, 0, 0.2)'
                                        : '0 2px 6px rgba(0, 0, 0, 0.16)',
                                    transition:
                                        'background-color 120ms ease, box-shadow 120ms ease',
                                }}
                            >
                                <Box
                                    component="span"
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body1,
                                        ),
                                        fontWeight: 600,
                                        fontSize: '1.05rem',
                                        letterSpacing: 0.15,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        flexWrap: 'nowrap',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1,
                                        color: 'inherit',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        style={{
                                            marginRight: theme.spacing(0.75),
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {isLoriRunning
                                            ? 'Auto Fill Running...'
                                            : 'Auto Fill with'}
                                    </Box>
                                    {!isLoriRunning && (
                                        <Box
                                            component="span"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                lineHeight: 0,
                                                transform: 'translateY(1px)',
                                            }}
                                        >
                                            <LoriText
                                                width={56}
                                                height={20}
                                                color="currentColor"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Button>
                            {isLoriRunning && (
                                <Typography
                                    variant="caption"
                                    style={{
                                        marginTop: theme.spacing(0.5),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Lori analysis is running for this unit.
                                </Typography>
                            )}
                        </Container>
                    )}
                </Container>

                <Container flex={1} direction="column" scrollY>
                    {props.loading ? (
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <CircularProgress />
                        </Container>
                    ) : areaInspectionList.length === 0 ? (
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                padding: theme.spacing(2),
                            }}
                        >
                            <span>No inspection areas available.</span>
                        </Container>
                    ) : (
                        <Container direction="column">
                            <Container
                                style={{
                                    flexWrap: 'wrap',
                                    padding: theme.spacing(1, 1, 0, 1),
                                }}
                            >
                                {areaInspectionList.map(
                                    (areaInspection, areaIndex) => (
                                        <Button
                                            key={`AREA_TAB_${areaInspection.id}`}
                                            variant={
                                                selectedAreaTab === areaIndex
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                                setSelectedAreaTab(areaIndex)
                                            }
                                            style={{
                                                textTransform: 'none',
                                                color:
                                                    selectedAreaTab ===
                                                    areaIndex
                                                        ? theme.palette.common
                                                              .white
                                                        : theme.palette.primary
                                                              .dark,
                                                borderColor:
                                                    selectedAreaTab ===
                                                    areaIndex
                                                        ? undefined
                                                        : theme.palette.primary
                                                              .dark,
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                                marginRight: theme.spacing(1),
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            {getAreaDisplayName(areaInspection)}
                                        </Button>
                                    ),
                                )}
                            </Container>
                            {selectedAreaInspectionContent}
                        </Container>
                    )}
                </Container>
                <Divider />
                <Container
                    style={{
                        padding: theme.spacing(2),
                        justifyContent: 'flex-end',
                    }}
                >
                    {unitInspection && editMode && (
                        <Container>
                            {unitInspection.complete_status ? (
                                <Button
                                    color="secondary"
                                    variant="outlined"
                                    style={{ marginRight: theme.spacing(2) }}
                                    onClick={() => {
                                        dispatch(
                                            updateUnitInspectionStatus({
                                                unitInspection: unitInspection,
                                                body: {
                                                    complete: false,
                                                },
                                            }),
                                        ).then(() => {
                                            if (setUnitInspection)
                                                setUnitInspection({
                                                    ...unitInspection,
                                                    complete_status: 0,
                                                })
                                        })
                                    }}
                                >
                                    Set Unit Incomplete
                                </Button>
                            ) : (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    style={{ marginRight: theme.spacing(2) }}
                                    onClick={() => {
                                        dispatch(
                                            updateUnitInspectionStatus({
                                                unitInspection: unitInspection,
                                                body: {
                                                    complete: true,
                                                },
                                            }),
                                        ).then(() => {
                                            if (setUnitInspection)
                                                setUnitInspection({
                                                    ...unitInspection,
                                                    complete_status: 1,
                                                })
                                        })
                                    }}
                                >
                                    Set Unit Complete
                                </Button>
                            )}
                        </Container>
                    )}
                    <Button
                        color="secondary"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Container>

                <Dialog
                    open={isAnalyzeWithLoriConfirmOpen}
                    onClose={closeAnalyzeWithLoriDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Auto Fill with Lori</DialogTitle>
                    <DialogContent dividers>
                        <ul
                            style={{
                                marginTop: 0,
                                paddingLeft: theme.spacing(3),
                                fontSize: '1.1rem',
                            }}
                        >
                            <li>
                                All videos for this unit inspection will be
                                used.
                            </li>
                            <li>Inventory statuses will be set by Lori.</li>
                            <li>
                                Messages and damages may also be created if the
                                inspectior explicity ask for them.
                            </li>
                            <li>
                                The process usually takes a few minutes to
                                complete.
                            </li>
                        </ul>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={closeAnalyzeWithLoriDialog}
                            color="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAnalyzeWithLori}
                            color="primary"
                            variant="contained"
                            disabled={isAnalyzeWithLoriDisabled}
                        >
                            Start Auto Fill
                        </Button>
                    </DialogActions>
                </Dialog>
            </SideDrawer>

            <MessageDrawer
                open={messagesOpen}
                onClose={() => {
                    setMessagesOpen(false)
                    setSelectedInvIns(undefined)
                }}
                onCreateMessage={() => {
                    if (selectedInvIns) {
                        dispatch(
                            updateInventoryInspection({
                                ...selectedInvIns,
                                message_count:
                                    (selectedInvIns.message_count ?? 0) + 1,
                            }),
                        )
                        // find the area ins for this inv ins
                        const updateAreaIns = unitInspection?.area_inspections.find(
                            (areaIns) => {
                                return selectedInvIns.area_ins_id === areaIns.id
                            },
                        )

                        if (updateAreaIns !== undefined && unitInspection) {
                            const updatedUnitIns = unitInspection
                            updatedUnitIns.area_inspections = unitInspection.area_inspections.map(
                                (areaIns) => {
                                    if (areaIns.id === updateAreaIns.id) {
                                        return {
                                            ...areaIns,
                                            message_count:
                                                areaIns.message_count + 1,
                                        }
                                    }
                                    return areaIns
                                },
                            )

                            dispatch(updateUnitInspection(updatedUnitIns))
                        }
                    }
                }}
                width={400}
            />
        </>
    )
}
